import { useState, useEffect } from 'react';
import { Award, TrendingUp, Sparkles, ChevronRight, Loader2 } from 'lucide-react';
import { Progress } from '../ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileCompletenessBar({ candidateId, candidate = null }) {
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (candidateId) {
      loadCompleteness();
    } else if (candidate) {
      // Calculate locally if candidate data is provided
      setCompleteness(calculateLocalCompleteness(candidate));
      setLoading(false);
    }
  }, [candidateId, candidate]);

  const loadCompleteness = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/candidates/${candidateId}/completeness`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompleteness(data);
      }
    } catch (error) {
      console.error('Failed to load completeness:', error);
    }
    setLoading(false);
  };

  // Local calculation fallback
  const calculateLocalCompleteness = (c) => {
    const weights = {
      name: 5, email: 5, phone: 5, date_of_birth: 3, nationality: 2,
      current_location: 3, linkedin_url: 2,
      current_company: 5, current_designation: 5, total_years_experience: 5,
      career_level: 3, current_industry: 3, professional_summary: 5,
      notice_period_days: 3, current_salary: 3, expected_salary: 3,
      technical_skills: 10, soft_skills: 5, languages_spoken: 5,
      education: 15, certifications: 5,
    };

    let score = 0;
    const total = Object.values(weights).reduce((a, b) => a + b, 0);
    const missing = [];

    Object.entries(weights).forEach(([field, points]) => {
      const value = c[field];
      const isFilled = Array.isArray(value) ? value.length > 0 : Boolean(value);
      if (isFilled) {
        score += points;
      } else {
        missing.push({ field, points });
      }
    });

    const percentage = Math.round((score / total) * 100);
    
    let tier, message;
    if (percentage >= 90) {
      tier = 'excellent';
      message = 'Excellent! Profile is in the top tier.';
    } else if (percentage >= 70) {
      tier = 'good';
      message = 'Good progress! Add more details to stand out.';
    } else if (percentage >= 50) {
      tier = 'average';
      message = 'Keep going! Complete profiles get 3x more views.';
    } else {
      tier = 'needs_work';
      message = 'Just getting started. Complete profile to unlock opportunities!';
    }

    return {
      percentage,
      tier,
      message,
      missing_fields: missing.sort((a, b) => b.points - a.points).slice(0, 10),
      pro_tip: percentage < 90 ? 'Profiles with 90%+ completeness are shown to top recruiters first and get 5x more interview calls!' : null
    };
  };

  const getProgressColor = () => {
    if (!completeness) return 'bg-slate-300';
    const { percentage } = completeness;
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-amber-500';
    if (percentage >= 50) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getTierBadge = () => {
    if (!completeness) return null;
    const { tier, percentage } = completeness;
    
    if (tier === 'excellent') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
          <Award className="w-3 h-3" /> Top Tier
        </span>
      );
    }
    return null;
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 animate-pulse">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-3"></div>
        <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded w-full mb-3"></div>
        <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
      </div>
    );
  }

  if (!completeness) return null;

  const { percentage, message, missing_fields, pro_tip, tier } = completeness;

  return (
    <div className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Profile Completeness</span>
          {getTierBadge()}
        </div>
        <span className={`text-lg font-bold ${
          percentage >= 90 ? 'text-green-600 dark:text-green-400' : 
          percentage >= 70 ? 'text-amber-600 dark:text-amber-400' : 
          'text-slate-600 dark:text-slate-400'
        }`}>
          {percentage}%
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full ${getProgressColor()} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Message */}
      <div className={`flex items-start gap-2 text-sm ${
        tier === 'excellent' ? 'text-green-600 dark:text-green-400' :
        tier === 'good' ? 'text-amber-600 dark:text-amber-400' :
        tier === 'average' ? 'text-orange-500 dark:text-orange-400' :
        'text-red-500 dark:text-red-400'
      }`}>
        <TrendingUp className="w-4 h-4 shrink-0 mt-0.5" />
        <p>{message}</p>
      </div>

      {/* Pro Tip */}
      {pro_tip && (
        <div className="mt-3 p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span><strong>Pro Tip:</strong> {pro_tip}</span>
          </p>
        </div>
      )}

      {/* Missing Fields (Expandable) */}
      {missing_fields && missing_fields.length > 0 && percentage < 90 && (
        <div className="mt-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
          >
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            {expanded ? 'Hide' : 'Show'} suggestions to improve ({missing_fields.length} fields)
          </button>
          
          {expanded && (
            <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Add these fields to boost your score:</p>
              <div className="flex flex-wrap gap-1.5">
                {missing_fields.slice(0, 8).map((item, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300"
                  >
                    {formatFieldName(item.field)}
                    <span className="text-amber-600 dark:text-amber-400 font-medium">+{item.points}</span>
                  </span>
                ))}
                {missing_fields.length > 8 && (
                  <span className="text-xs text-slate-400">+{missing_fields.length - 8} more</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
